import { useMemo } from 'react';
import { Autocomplete, TextField, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled, SxProps } from '@mui/material/styles';
import { FilterChip } from '@/components/shared/FilterAndSearchPanel/styles';
import { ScrollableChipsContainer } from '@/components/shared/ScrollableChipsContainer';
import { CalculationSummary } from '@/components/shared/CalculationSummary';
import { useCalculationData } from '@/hooks/useCalculationData';
import { useUnitConversion } from '@/hooks/useUnitConversion';
import { UnitsEnum } from '@/__generated__/graphql';
import * as localState from '../state';
import { useAtom } from 'jotai';

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  width: '100%',
  '& .MuiAutocomplete-input:focus': {
    boxShadow: 'none',
    border: '0px solid black',
  },
  '& .MuiAutocomplete-tag': {
    margin: 0,
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  '& .MuiAutocomplete-tag:last-child': {
    marginRight: 0,
  },
}));

// API Response types
export interface AreaAndType {
  value: number;
  type: string;
  __typename?: string;
}

export interface PaintBlock {
  name: string;
  paintRegionId: string;
  totalArea?: (AreaAndType | null)[] | null;
  totalCountByPoiType?: ({
    value: number;
    type: string;
    __typename?: string;
  } | null)[] | null;
  __typename?: string;
}

export interface DefectsAreaResponse {
  structureId: string;
  defectsByPaintBlock: PaintBlock[];
  totalCount: number;
  __typename: string;
}

export interface ApiResponse {
  data: {
    defectsAreaForAssemblyPaintRegionsByGroup: DefectsAreaResponse;
  };
}

interface Props{
  sx?: SxProps;
  onSearch: (value: string) => void;
  value: string;
  apiData?: ApiResponse;
  loading?: boolean;
  isSavedBlocks?: boolean;
}

export const CustomBlockListSearch = ({
  onSearch,
  value: textFieldValue,
  apiData,
  isSavedBlocks = false,
  ...textFieldProps
}: Props) => {
  const [customBlockCuboids, setCustomBlockCuboids] = useAtom(localState.customBlockCuboids);

  const selectedBlockNames = useMemo(
    () => Array.from(customBlockCuboids.values())
      .map(cuboid => cuboid.name)
      .filter((name): name is string => name !== undefined),
    [customBlockCuboids]
  );

  const defectsData = apiData?.data?.defectsAreaForAssemblyPaintRegionsByGroup;
  
  const calculations = useCalculationData({
    data: defectsData,
    selectedItems: selectedBlockNames,
    getItemName: (block: PaintBlock) => block.name,
    getPrimaryValue: (block: PaintBlock) => {
      const ri5Item = block?.totalArea?.find(item => item?.type === 'Ri5' || item?.type === 'ri5');
      return ri5Item?.value || 0;
    },
    getTotalValue: (block: PaintBlock) => {
      const areaItem = block?.totalArea?.find(item => item?.type === 'area' || item?.type === 'Area');
      return areaItem?.value || 0;
    },
    getTotalCount: (data: DefectsAreaResponse) => data?.totalCount || 0,
    getItems: (data: DefectsAreaResponse) => data?.defectsByPaintBlock || [],
    unit: UnitsEnum.SquareMeter, // Pass the unit for conversion
  });

  const { getUnitSuffix } = useUnitConversion();
  const areaUnitSuffix = getUnitSuffix({ unit: UnitsEnum.SquareMeter });

  const handleDelete = (chipToDelete: unknown) => {
    const found = customBlockCuboids.find((cuboid) => cuboid.id === chipToDelete).at(0);
    if (!found || typeof found === 'string') return;

    setCustomBlockCuboids((current) => {
      const copy = current.asMutableStateMap();
      copy.delete(found.id);
      return copy.asStateMap();
    });
  };

  const selectedChips = useMemo(
    () => customBlockCuboids.map((cuboid) => ({ name: cuboid.name ?? '', id: cuboid.id })),
    [customBlockCuboids]
  );

  return (
    <Box sx={{ width: '100%', maxWidth: 700, margin: '0 auto' }}>
      <StyledAutocomplete
        multiple
        id="chips-autocomplete"
        options={selectedChips}
        freeSolo
        disableClearable
        onInputChange={(event, newInputValue) => {
          onSearch(newInputValue);
        }}
        renderTags={(value, getTagProps) => (
          <ScrollableChipsContainer>
            {value.map((option: unknown, index) => {
              const typedOption = option as { name: string; id: string };
              return (
                <FilterChip
                  {...getTagProps({ index })}
                  label={typedOption.name}
                  key={index}
                  deleteIcon={<CloseIcon />}
                  onDelete={() => handleDelete(typedOption.id)}
                  style={{ margin: '2px' }}
                />
              );
            })}
          </ScrollableChipsContainer>
        )}
        value={selectedChips}
        renderInput={(parameters) => (
          <TextField
            {...textFieldProps}
            {...parameters}
            onChange={(event) => {
              onSearch(event.target.value);
            }}
            value={textFieldValue}
            variant="standard"
            placeholder={selectedChips.length > 0 ? '' : 'Search blocks'}
          />
        )}
      />

      {!isSavedBlocks && (
        <CalculationSummary
          calculations={{
            totalBlocks: calculations.totalItems,
            selectedBlocks: calculations.selectedItems,
            ri5Area: calculations.primaryValue,
            totalArea: calculations.totalValue,
            percentage: calculations.percentage,
            hasData: calculations.hasData,
          }}
          unit={areaUnitSuffix}
          loading={false}
        />)}
    </Box>
  );
};
